import qs from 'qs';

import type {
  FetchCategoryByKeyArgs,
  FetchCategoryByIdArgs,
  EditCategoryByIdArgs,
  FetchCategoriesArgs,
  CreateCategoryArgs,
} from './types/categories.types';
import type {
  BaseApiPaginatedResponse,
  BaseApiResponse,
} from './types/api.types';

import {
  mapCategoryFromApi,
  mapCategoryToApi,
} from './mappers/categories.mappers';
import {
  type ApiCategory,
  type UiCategory,
} from '../schemas/categories.schema';
import { patch, post, get } from './axios';

export const fetchCategories = async ({
  appliesTo,
  filters,
  organizationId,
  organizationIds,
  withPlatform,
}: FetchCategoriesArgs) => {
  const queryString = qs.stringify(
    {
      ...filters,
      ...(appliesTo ? { appliesTo } : {}),
      ...(organizationId && withPlatform ? { includePlatform: 'true' } : {}),
      ...(organizationId && organizationIds ? { organizationIds } : {}),
    },
    { arrayFormat: 'repeat' },
  );

  const response = await get<BaseApiPaginatedResponse<ApiCategory[]>>(
    `/v2/${organizationId ? `organizations/${organizationId}/` : ''}categories?${queryString}`,
  );
  return {
    ...response,
    data: response?.data?.map(mapCategoryFromApi) as UiCategory[],
  };
};

export const fetchPublicCategories = async ({
  filters,
}: Omit<
  FetchCategoriesArgs,
  'appliesTo' | 'organizationId' | 'withPlatform'
>) => {
  const response = await get<BaseApiPaginatedResponse<ApiCategory[]>>(
    `/v2/public/categories`,
    {
      query: {
        ...filters,
      },
    },
  );
  return {
    ...response,
    data: response?.data?.map(mapCategoryFromApi) as UiCategory[],
  };
};

export async function fetchCategoryById({
  id,
  organizationId,
}: FetchCategoryByIdArgs): Promise<UiCategory | null> {
  const response = await get<BaseApiResponse<ApiCategory>>(
    `/v2/${organizationId ? `organizations/${organizationId}/` : ''}categories/${id}`,
  );
  if (!response?.data) return null;
  return mapCategoryFromApi(response?.data);
}
export async function fetchCategoryByKey({ key }: FetchCategoryByKeyArgs) {
  const response = await get<BaseApiResponse<ApiCategory>>(
    `/v2/categories/key/${key}`,
  );
  if (!response?.data) return null;
  return mapCategoryFromApi(response?.data);
}

export async function createCategory({
  organizationId,
  ...args
}: CreateCategoryArgs): Promise<UiCategory | null> {
  const response = await post<ApiCategory>(
    `/v2/${organizationId ? `organizations/${organizationId}/` : ''}categories`,
    mapCategoryToApi(args),
  );
  if (!response?.data) return null;
  return mapCategoryFromApi(response?.data);
}

export async function editCategoryById({
  id,
  organizationId,
  ...args
}: EditCategoryByIdArgs): Promise<UiCategory | null> {
  const response = await patch<ApiCategory>(
    `/v2/${organizationId ? `organizations/${organizationId}/` : ''}categories/${id}`,
    mapCategoryToApi({ ...args }),
  );
  if (!response?.data) return null;
  return mapCategoryFromApi(response?.data);
}
